import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Loader,
  ErrorFallback,
  buildNotification,
  GenerateNotification,
} from 'smart-react';
import { useAuth } from '../../../Core/Context/AuthContext';
import { isAdminRole } from '../../../../Utils/Auth/ValidatePermissions';
import NoPermission from '../../../Core/NoPermission/NoPermission';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import {
  deleteLookup,
  listLookupByType,
} from '../Services/LookupDetailService';
import AddLookupDetails from '../Components/ContentSlider/AddLookupDetails';
import {
  TreeList,
  orderBy,
  filterBy,
  mapTree,
  extendDataItem,
} from '@progress/kendo-react-treelist';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../constants/eventDataTypes';
import {
  objectToBase64,
  base64ToObject,
} from '../../../../Utils/Common/commonUtils';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import {
  ERROR_MESSAGE,
  DELETE_MESSAGE,
} from '../../../../constants/notificationMessages';
import DataColumns from '../Components/DataColumns/DataColumns'; // Import the column function

const subItemsField = 'children';
const expandField = 'expanded';
const hasChildrenField = 'hasChildren';

/**
 * AdminLookupDetails main screen
 * @param {Object} props - Component props
 * @param {Object} props.lookup - Lookup object containing type information
 * @returns {React.ReactElement} The AdminLookupDetails component.
 */
const AdminLookupDetails = ({ lookup }) => {
  const [loading, setLoading] = React.useState(false);
  const { user } = useAuth();
  const { getParam, updateParams } = useQueryParams();
  const editLookup = getParam('editLookup');

  const [showSlider, setShowSlider] = React.useState(false);
  const [isRefresh, setIsRefresh] = React.useState(false);
  const [selectedParent, setSelectedParent] = React.useState(null);
  const [selectedLookup, setSelectedLookup] = React.useState(null);
  const [state, setState] = React.useState({
    data: [],
    dataState: {
      sort: [],
      filter: [],
    },
    expanded: [],
  });

  const [editMode, setEditMode] = React.useState(false); // State for step arguments

  const [visible, setVisible] = React.useState(false);
  const [selectedItemForDeletion, setSelectedItemForDeletion] =
    React.useState(null);

  /**
   * handle Data State Change
   * @event object
   */
  const handleDataStateChange = (event) => {
    setState((prevState) => ({
      ...prevState,
      dataState: event.dataState,
    }));
  };

  React.useEffect(() => {
    if (editLookup) {
      let tempData = base64ToObject(editLookup);
      if (tempData) {
        setSelectedLookup(tempData);
        setEditMode(true);
        setShowSlider(true);
      }
    }
  }, []);

  /**
   * handle expand change
   * @e object
   */
  const onExpandChange = (e) => {
    const { dataItem } = e;
    const itemId = dataItem.id;
    const isExpanding = !state.expanded.includes(itemId);

    if (isExpanding) {
      // Add to expanded list when expanding
      setState((prevState) => ({
        ...prevState,
        expanded: [...prevState.expanded, itemId],
      }));
    } else {
      // Remove from expanded list when collapsing
      setState((prevState) => ({
        ...prevState,
        expanded: prevState.expanded.filter((id) => id !== itemId),
      }));
    }
  };

  /**
   * add expand field
   * @dataTree Array
   */
  const addExpandField = (dataTree) => {
    const expanded = state.expanded;
    return mapTree(dataTree, subItemsField, (item) =>
      extendDataItem(item, subItemsField, {
        [expandField]: expanded.includes(item.id),
      }),
    );
  };

  /**
   * process Data
   */
  const processData = () => {
    const { data, dataState } = state;
    const filteredData = filterBy(data, dataState.filter, subItemsField);
    const sortedData = orderBy(filteredData, dataState.sort, subItemsField);
    return addExpandField(sortedData);
  };

  /**
   * featch lookup details
   */
  const fetchLookupDetails = async () => {
    try {
      setLoading(true);

      // Fetch the complete hierarchy using the new service method
      const response = await listLookupByType({
        moduleName: 'AdminLookupDetails',
        isServiceWorker: false,
        listController: 'LookupController',
        data: {
          ...lookup,
        },
      });

      setState((prevState) => ({
        ...prevState,
        data: response?.Lookups,
      }));
    } catch (error) {
      console.error('Error fetching lookup details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh the data
  const refreshData = () => {
    setState({
      data: [],
      dataState: {
        sort: [{ field: 'DisplayOrder', dir: 'asc' }],
        filter: [],
      },
      expanded: [],
    });
    fetchLookupDetails();
  };

  /**
   * Handles adding a child step.
   * @param {TreeNode} parentItem - The parent tree node to which a child is added.
   */
  const onEditItem = (parentItem) => {
    const tempDataItem = {
      Type: parentItem?.Type,
      LookupId: parentItem?.LookupId,
      DisplayName: parentItem?.DisplayName,
      Value: parentItem?.Value,
      Active: parentItem?.Active,
      NumberOfChilds: parentItem?.NumberOfChilds,
      DisplayOrder: parentItem?.DisplayOrder,
    };
    updateParams({ editLookup: objectToBase64(tempDataItem) });
    setSelectedLookup(parentItem);
    setEditMode(true);
    setShowSlider(true);
  };

  /**
   * Opens the delete confirmation dialog and sets the selected item.
   * @param {Object} item - The selected tree node to delete.
   */
  const deleteFlowStepDialog = (item) => {
    setSelectedItemForDeletion(item);
    setVisible(true);
  };

  /**
   * Deletes a tree node by ID.
   */
  const onDelete = async () => {
    if (!selectedItemForDeletion) return;
    setVisible(false);
    setLoading(true);
    let data = selectedItemForDeletion;
    let response = await deleteLookup({
      data,
      isServiceWorker: false,
    });
    try {
      if (response?.IsSuccess) {
        GenerateNotification(
          buildNotification(DELETE_MESSAGE),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setSelectedItemForDeletion(null);
      setIsRefresh(!isRefresh);
      setLoading(false);
    }
  };

  /**
   * Defines the columns for the AppFlowSteps TreeList by calling the `getAppFlowColumns` function.
   */
  const getColumns = DataColumns({
    onEditItem,
    deleteFlowStepDialog,
  });

  React.useEffect(() => {
    if (lookup) {
      fetchLookupDetails();
    }
  }, [lookup, isRefresh]);

  if (!lookup) {
    return (
      <div className='container mx-auto p-4'>
        <div className='text-center text-gray-500'>No lookup type selected</div>
      </div>
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        refreshData();
      }}
    >
      <div className='container mx-auto'>
        {visible && (
          <Dialog title={'Please confirm'} onClose={() => setVisible(!visible)}>
            <p
              style={{
                margin: '25px',
                textAlign: 'center',
              }}
            >
              Are you sure you want delete this lookup?
            </p>
            <DialogActionsBar>
              <button
                className='k-button k-button-md k-rounded-md k-button-solid k-button-solid-base'
                onClick={() => setVisible(!visible)}
              >
                No
              </button>
              <button
                className='k-button k-button-md k-button-solid k-button-solid-primary k-rounded-md'
                onClick={onDelete}
              >
                Yes
              </button>
            </DialogActionsBar>
          </Dialog>
        )}
        {!isAdminRole(user) && <NoPermission />}
        {isAdminRole(user) && (
          <React.Suspense fallback={<Loader />}>
            <div>
              {/* Loading Overlay */}
              {loading && <Loader />}
              <div className='k-py-3 k-border-0 add-app-flow-step-button'>
                <Button
                  themeColor={'primary'}
                  className='k-mx-1 k-float-right !k-mb-2'
                  onClick={() => {
                    setShowSlider(true);
                    setEditMode(false);
                    setSelectedParent(lookup);
                    setSelectedLookup(null);
                  }}
                >
                  <FontAwesomeIcon className='k-mr-2' icon={faPlus} /> Add
                </Button>
              </div>
              <div className='k-clear-both' />
              {/* TreeList */}
              <div className='k-mt-2'>
                <TreeList
                  style={{
                    maxHeight: '600px',
                    overflow: 'auto',
                  }}
                  expandField={expandField}
                  subItemsField={subItemsField}
                  hasChildrenField={hasChildrenField}
                  onExpandChange={onExpandChange}
                  sortable={{
                    mode: 'multiple',
                  }}
                  filterable={true}
                  {...state.dataState}
                  data={processData()}
                  onDataStateChange={handleDataStateChange}
                  columns={getColumns}
                />
              </div>
            </div>
            {showSlider && (
              <AddLookupDetails
                show={showSlider}
                selectedParent={selectedParent}
                selectedLookup={selectedLookup}
                setShowSlider={setShowSlider}
                editMode={editMode}
                setIsRefresh={setIsRefresh}
                isRefresh={isRefresh}
                editType={false}
              />
            )}
          </React.Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default AdminLookupDetails;
